import { useState, useEffect } from "react";
import {
  CANCELLED_BY_ADMIN,
  CONFIRMED,
  DELETED,
  ERROR,
  GREEN,
  ORANGE,
  RED,
  SESSION_EXPIRED,
  SOMETHING_WENT_WRONG,
  priceRound,
  showSnackbar,
} from "../../utils";
import { useNavigate } from "react-router-dom";
import { getAgentData } from "../api/agent";
import RoutesList from "../constants/RoutesList";

const SkeletonCell = ({ width = "80%" }) => (
  <td>
    <span
      style={{
        display: "inline-block",
        height: 13,
        width,
        borderRadius: 6,
        background: "linear-gradient(90deg, #f0ebe0 25%, #e8e0cc 50%, #f0ebe0 75%)",
        backgroundSize: "400px 100%",
        animation: "topbar-shimmer 1.4s infinite linear",
      }}
    />
  </td>
);

const SkeletonRow = () => (
  <tr>
    <SkeletonCell width="90px" />
    <SkeletonCell width="70px" />
    <SkeletonCell width="80px" />
    <SkeletonCell width="50px" />
    <SkeletonCell width="65px" />
    <SkeletonCell width="20px" />
  </tr>
);

export default function RecentBookings() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const results = await getAgentData();
      setData(results?.bookings);
    } catch (err) {
      if (err === "sessionTimeout") {
        showSnackbar(SESSION_EXPIRED, ERROR);
        localStorage.removeItem("Token");
        navigate(RoutesList.login);
      } else {
        showSnackbar(SOMETHING_WENT_WRONG, ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onPressAction = (id) => {
    navigate(`${RoutesList.bookingDetails}?booking_id=${id}`);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const bookings = Array.isArray(data) ? data : [];
  const hasBookings = bookings.length > 0;

  return (
    <section id="dashboard_main_arae" className="section_padding">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div
              className="dashboard_common_table"
              style={{
                background: "#fffdf7",
                borderRadius: 24,
                padding: 24,
                boxShadow: "0 10px 30px rgba(30, 20, 5, 0.08)",
                border: "1px solid rgba(200, 162, 74, 0.15)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  marginBottom: 18,
                  paddingBottom: 14,
                  borderBottom: "1px solid rgba(200, 162, 74, 0.18)",
                }}
              >
                <div>
                  <h3 style={{ marginBottom: 6 }}>Recent bookings</h3>
                  <p style={{ margin: 0, color: "#6c6f85", fontSize: 14 }}>
                    Your latest reservations will show up here once you book a flight.
                  </p>
                </div>
                {hasBookings ? (
                  <div
                    style={{
                      background: "#c8a24a",
                      color: "#17130b",
                      borderRadius: 999,
                      padding: "8px 14px",
                      fontSize: 13,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {bookings.length} booking{bookings.length === 1 ? "" : "s"}
                  </div>
                ) : null}
              </div>

              {isLoading || hasBookings ? (
                <div className="table-responsive-lg table_common_area">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Destination</th>
                        <th>Departure Date</th>
                        <th>Booking amount</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading
                        ? [0, 1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)
                        : bookings
                        .slice(-5)
                        .reverse()
                        .map((booking) => {
                          return (
                            <tr>
                              <td>{booking?.reference_number ?? '-'}</td>
                              <td>
                                {booking?.onward_departure_airport?.code}
                                {' - '}
                                {booking.onward_arrival_airport?.code}
                              </td>
                              <td>{booking?.onward_departure_date}</td>
                              <td>
                                €
                                {booking.total !== undefined
                                  ? priceRound(booking?.total)
                                  : 0}
                              </td>
                              <td
                                className="complete"
                                style={{
                                  fontWeight: 'bold',
                                  color:
                                    booking?.status === CONFIRMED
                                      ? GREEN
                                      : booking?.status === DELETED
                                      ? RED
                                      : ORANGE,
                                }}
                              >
                                {booking?.status === DELETED
                                  ? CANCELLED_BY_ADMIN
                                  : booking?.status}
                              </td>
                              <td>
                                <i
                                  onClick={() => {
                                    onPressAction(booking?.reference_number);
                                  }}
                                  className="fas fa-eye"
                                ></i>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    placeItems: "center",
                    minHeight: 220,
                    textAlign: "center",
                    padding: "24px 16px 10px",
                  }}
                >
                  <div
                    style={{
                      width: 84,
                      height: 84,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ffffff 0%, #edf0ff 100%)",
                      display: "grid",
                      placeItems: "center",
                      boxShadow: "0 12px 30px rgba(19, 33, 68, 0.08)",
                      marginBottom: 18,
                      color: "#343a40",
                      fontSize: 28,
                    }}
                  >
                    <i className="fas fa-ticket-alt" />
                  </div>
                  <h4 style={{ marginBottom: 10, color: "#2b2540" }}>
                    No recent bookings yet
                  </h4>
                  <p
                    style={{
                      maxWidth: 520,
                      margin: "0 auto 18px",
                      color: "#6c6f85",
                      lineHeight: 1.7,
                    }}
                  >
                    Once you complete a flight reservation, the latest booking
                    reference, route, travel date, fare, and status will appear
                    here for quick access.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
